import './BattingStatChart.scss';

import { useMemo } from 'react';

import { Typography } from '@trackman/web-shared-components';

import { BattingStatChartDataDto, PlayerStatChartConfigDto } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { useGetUnitSystem } from 'hooks/helperHooks';

const BattingStatChart = ({ chartData, isLoading, chartConfig }: ChartProps<BattingStatChartDataDto, PlayerStatChartConfigDto>) => {
  const unitSystem = useGetUnitSystem();

  const value = useMemo(() => {
    if (chartData?.battingStatValue == null || isLoading) {
      return '-';
    }

    switch (chartConfig.playerStat) {
      case 'ExitSpeed':
      case 'MaxExitSpeed':
      case 'LaunchAngle':
      case 'SwingPercent':
      case 'ContactPercent':
      case 'ChasePercent':
      case 'WhiffPercent':
      case 'FlyBallPercent':
      case 'GroundBallPercent':
        return chartData.battingStatValue.toFixed(1);
      case 'BattingAverage':
        return chartData.battingStatValueString ?? '-';
      case 'OnBasePercent':
      case 'Slugging':
      case 'OnBasePlusSlugging':
        return chartData.battingStatValue.toFixed(3).replace(/^0+/, '');
      default:
        return chartData.battingStatValue;
    }
  }, [isLoading, chartData, chartConfig?.playerStat]);

  const unit = useMemo(() => {
    switch (chartConfig.playerStat) {
      case 'ExitSpeed':
      case 'MaxExitSpeed':
        return unitSystem === 'Metric' ? 'kph' : 'mph';
      case 'LaunchAngle':
        return 'deg';
      case 'SwingPercent':
      case 'ContactPercent':
      case 'ChasePercent':
      case 'WhiffPercent':
      case 'FlyBallPercent':
      case 'GroundBallPercent':
        return '%';
      case 'BattingAverage':
        return chartData?.battingStatValue != null ? `(${chartData.battingStatValue.toFixed(3).replace(/^0+/, '')})` : '';
      default:
        return '';
    }
  }, [chartData?.battingStatValue, chartConfig?.playerStat]);

  return (
    <div className='BattingStatChart-container'>
      <div className='BattingStatChart-value-wrapper'>
        <Typography variant='h1' component='div' className='BattingStatChart-value-label'>
          {value}
        </Typography>
        {unit && (
          <Typography variant='body' component='div' className='BattingStatChart-unit-label'>
            {unit}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default BattingStatChart;
